import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getChapterById, type BlogChapter } from '../data/blogChapters';

gsap.registerPlugin(ScrollTrigger);

export class BlogDetailView {
  private root: HTMLElement | null = null;
  private backdrop: HTMLElement | null = null;
  private inner: HTMLElement | null = null;
  private backBtn: HTMLButtonElement | null = null;
  private chapterLabel: HTMLElement | null = null;
  private heroImage: HTMLImageElement | null = null;
  private heroNum: HTMLElement | null = null;
  private titleEl: HTMLElement | null = null;
  private excerptEl: HTMLElement | null = null;
  private metaEl: HTMLElement | null = null;
  private highlightsTitle: HTMLElement | null = null;
  private highlightsList: HTMLElement | null = null;
  private bodyContainer: HTMLElement | null = null;

  private entranceTimeline: gsap.core.Timeline | null = null;
  private exitTimeline: gsap.core.Timeline | null = null;
  private scrollTriggers: ScrollTrigger[] = [];

  private previousActiveElement: HTMLElement | null = null;
  private boundClose: () => void;
  private boundBackdropClick: (e: MouseEvent) => void;
  private boundKeydown: (e: KeyboardEvent) => void;
  private reduceMotion: boolean = false;

  constructor() {
    this.boundClose = this.close.bind(this);
    this.boundBackdropClick = this.handleBackdropClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);

    if (typeof window !== 'undefined') {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    this.root = document.getElementById('blog-detail-view');
    if (!this.root) return;

    this.backdrop = this.root.querySelector('.blog-detail-backdrop');
    this.inner = this.root.querySelector('.blog-detail-inner');
    this.backBtn = this.root.querySelector('.blog-detail-back');
    this.chapterLabel = this.root.querySelector('.blog-detail-chapter-label');
    this.heroImage = this.root.querySelector('.blog-detail-hero-image');
    this.heroNum = this.root.querySelector('.blog-detail-hero-num');
    this.titleEl = this.root.querySelector('.blog-detail-title');
    this.excerptEl = this.root.querySelector('.blog-detail-excerpt');
    this.metaEl = this.root.querySelector('.blog-detail-meta');
    this.highlightsTitle = this.root.querySelector('.blog-detail-highlights-title');
    this.highlightsList = this.root.querySelector('.blog-detail-highlights-list');
    this.bodyContainer = this.root.querySelector('.blog-detail-body');

    if (this.backBtn) {
      this.backBtn.addEventListener('click', this.boundClose);
    }
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.boundBackdropClick);
    }
  }

  public open(chapterId: number): void {
    const chapter = getChapterById(chapterId);
    if (!chapter || !this.root || !this.inner) return;

    this.previousActiveElement = (document.activeElement as HTMLElement) || null;
    this.populateContent(chapter);

    this.root.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.boundKeydown);

    if (this.inner) {
      this.inner.scrollTop = 0;
    }

    const duration = this.reduceMotion ? 0.01 : 0.35;
    const stagger = this.reduceMotion ? 0 : 0.08;

    this.entranceTimeline?.kill();
    this.entranceTimeline = gsap.timeline({
      onComplete: () => {
        this.entranceTimeline = null;
        this.backBtn?.focus();
        this.setupScrollAnimations();
      },
    });

    gsap.set(this.backdrop, { opacity: 0 });
    gsap.set(this.inner, { opacity: 0, y: 24 });

    this.entranceTimeline
      .to(this.backdrop, { opacity: 1, duration, ease: 'power2.out' })
      .to(
        this.inner,
        { opacity: 1, y: 0, duration: this.reduceMotion ? 0.01 : 0.5, ease: 'back.out(1.1)' },
        '-=0.15'
      );

    const animatables = this.root.querySelectorAll(
      '.blog-detail-hero, .blog-detail-title, .blog-detail-excerpt, .blog-detail-meta, .blog-detail-highlights, .blog-detail-body'
    );
    if (animatables.length && !this.reduceMotion) {
      gsap.set(animatables, { opacity: 0, y: 16 });
      this.entranceTimeline.to(animatables, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger,
        ease: 'power2.out',
      }, '-=0.2');
    }
  }

  public close(): void {
    if (!this.root) return;

    this.killScrollTriggers();
    this.entranceTimeline?.kill();
    this.entranceTimeline = null;

    const duration = this.reduceMotion ? 0.01 : 0.2;

    this.exitTimeline?.kill();
    this.exitTimeline = gsap.timeline({
      onComplete: () => {
        this.exitTimeline = null;
        this.root?.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', this.boundKeydown);
        if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
          this.previousActiveElement.focus();
        }
        this.previousActiveElement = null;
      },
    });

    this.exitTimeline
      .to(this.inner, { opacity: 0, y: 12, duration, ease: 'power2.in' })
      .to(this.backdrop, { opacity: 0, duration: duration * 1.2 }, '-=0.05');
  }

  private populateContent(chapter: BlogChapter): void {
    if (this.chapterLabel) this.chapterLabel.textContent = `Chapter ${chapter.chapterNum}`;
    if (this.heroNum) this.heroNum.textContent = chapter.chapterNum;
    if (this.heroImage) {
      this.heroImage.src = chapter.imageUrl;
      this.heroImage.alt = chapter.title;
    }
    if (this.titleEl) this.titleEl.textContent = chapter.title;
    if (this.excerptEl) this.excerptEl.textContent = chapter.excerpt;
    if (this.metaEl) this.metaEl.textContent = chapter.readTime;
    if (this.highlightsTitle) this.highlightsTitle.textContent = 'In this chapter';
    if (this.highlightsList) {
      this.highlightsList.innerHTML = chapter.highlights
        .map((h) => `<li>${h}</li>`)
        .join('');
    }
    if (this.bodyContainer) {
      this.bodyContainer.innerHTML = chapter.body.map((p) => `<p>${p}</p>`).join('');
    }
  }

  private setupScrollAnimations(): void {
    if (this.reduceMotion || !this.bodyContainer) return;

    const paragraphs = this.bodyContainer.querySelectorAll('p');
    if (!paragraphs.length) return;

    paragraphs.forEach((p) => {
      gsap.set(p, { opacity: 0, y: 20 });
      const st = ScrollTrigger.create({
        trigger: p,
        start: 'top 92%',
        end: 'top 60%',
        once: true,
        onEnter: () => {
          gsap.to(p, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' });
        },
      });
      this.scrollTriggers.push(st);
    });
  }

  private killScrollTriggers(): void {
    this.scrollTriggers.forEach((st) => st.kill());
    this.scrollTriggers = [];
  }

  private handleBackdropClick(e: MouseEvent): void {
    if (e.target === this.backdrop) {
      this.close();
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close();
    }
  }

  public dispose(): void {
    this.killScrollTriggers();
    this.entranceTimeline?.kill();
    this.exitTimeline?.kill();
    this.entranceTimeline = null;
    this.exitTimeline = null;
    this.backBtn?.removeEventListener('click', this.boundClose);
    this.backdrop?.removeEventListener('click', this.boundBackdropClick);
    document.removeEventListener('keydown', this.boundKeydown);
    this.root = null;
    this.backdrop = null;
    this.inner = null;
    this.backBtn = null;
    this.chapterLabel = null;
    this.heroImage = null;
    this.heroNum = null;
    this.titleEl = null;
    this.excerptEl = null;
    this.metaEl = null;
    this.highlightsTitle = null;
    this.highlightsList = null;
    this.bodyContainer = null;
    this.previousActiveElement = null;
  }
}
